import { Button, chakra, Input, SpaceProps, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { rem } from 'polished';
import { useState } from 'react';
import { useUITranslation } from '../../../context/TranslationProvider';

import { useNewsletterSubscriptionState } from '../NewsletterSubscription.state';
import { SubmitHandler, useForm } from 'react-hook-form';
import { formSchema } from './Form.utils';
import { FormSchemaType } from './Form.types';

const ENDPOINT = process.env.NEXT_PUBLIC_NEWSLETTER_SUBSCRIPTION_ENDPOINT;

export const NewsletterSubscriptionForm = ({ ...spaceProps }: SpaceProps) => {
  const { t } = useUITranslation();
  const validationSchema = formSchema(t);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { email: '' }, resolver: zodResolver(validationSchema) });

  const setSubmittedEmail = useNewsletterSubscriptionState(state => state.setEmail);

  const [generalError, setGeneralError] = useState<string | undefined>(() =>
    ENDPOINT ? undefined : '⚠️ Set NEXT_PUBLIC_NEWSLETTER_SUBSCRIPTION_ENDPOINT in your .env file',
  );

  const onSubmit: SubmitHandler<FormSchemaType> = async ({ email }) => {
    if (!ENDPOINT) return;

    setGeneralError(undefined);

    const data = new FormData();
    data.append('email', email);

    fetch(new Request(ENDPOINT, { method: 'POST', body: data })).then(response => {
      if (!response.ok) {
        setGeneralError(t('newsletter.error.request'));
        return;
      }
      setSubmittedEmail(email);
    });
  };

  return (
    <chakra.form
      display={{ sm: 'flex', md: 'block', lg: 'flex' }}
      position="relative"
      flexDirection="row"
      flexWrap="wrap"
      noValidate
      onChange={() => setGeneralError(undefined)}
      onSubmit={handleSubmit(onSubmit)}
      {...spaceProps}
    >
      <Input
        {...register('email')}
        variant="round"
        placeholder={t('common.newsletter.input')}
        textAlign={{ base: 'center', sm: 'left' }}
        borderRightRadius={{ sm: 0, md: 'full', lg: 0 }}
        borderRightWidth={{ sm: 0, md: '2px', lg: 0 }}
        flex="1"
        mr={{ sm: rem(-24), md: undefined, lg: rem(-24) }}
        pr={{ sm: rem(29), md: undefined, lg: rem(29) }}
        height={{ base: rem(50), sm: rem(56), md: rem(50), lg: rem(56) }}
        isInvalid={!!errors.email || !!generalError}
        isDisabled={isSubmitting || !ENDPOINT}
        cursor={isSubmitting ? 'progress' : undefined}
        _focusVisible={{ boxShadow: 'outline' }}
      />

      <Button
        type="submit"
        variant="primary"
        width={{ base: 'full', sm: 'auto', md: 'full', lg: 'auto' }}
        mt={{ base: 'space.16', sm: 0, md: 'space.16', lg: 0 }}
        flex="0 0 auto"
        lineHeight={{ base: rem(48), sm: rem(54), md: rem(48), lg: rem(54) }}
        isLoading={isSubmitting}
        isDisabled={isSubmitting || !ENDPOINT}
      >
        {t('common.newsletter.submit')}
      </Button>

      <Text
        position={{ md: 'absolute' }}
        top={{ md: '100%' }}
        textStyle="error"
        width="full"
        pl="space.28"
        pr="space.16"
        marginTop={{ base: 'space.10', sm: 'space.16', md: 'space.10', lg: 'space.16' }}
        role="alert"
        data-testid="newsletter-error-message"
        display={errors.email?.message || generalError || !ENDPOINT ? 'block' : 'none'}
      >
        {generalError || errors.email?.message}
      </Text>
    </chakra.form>
  );
};
